import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

type ToastType = 'success' | 'error' | 'warning' | 'info';
type Toast = { id: number; type: ToastType; message: string; };

const ToastContext = createContext<{
    toast: (type: ToastType, message: string) => void;
}>({ toast: () => { } });

export const useToast = () => useContext(ToastContext);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const counterRef = useRef(0);

    const toast = useCallback((type: ToastType, message: string) => {
        const id = ++counterRef.current;
        setToasts(prev => [...prev, { id, type, message }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
    }, []);

    const dismiss = (id: number) => setToasts(prev => prev.filter(t => t.id !== id));

    const icons = { success: CheckCircle, error: AlertCircle, warning: AlertTriangle, info: Info };
    const colors = {
        success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
        error: 'bg-red-50 border-red-200 text-red-800',
        warning: 'bg-amber-50 border-amber-200 text-amber-800',
        info: 'bg-blue-50 border-blue-200 text-blue-800',
    };
    const iconColors = {
        success: 'text-emerald-500', error: 'text-red-500', warning: 'text-amber-500', info: 'text-blue-500',
    };

    return (
        <ToastContext.Provider value={{ toast }}>
            {children}
            {/* Toast Container */}
            <div className="fixed top-4 right-4 z-[100] space-y-2 max-w-sm">
                {toasts.map(t => {
                    const Icon = icons[t.type];
                    return (
                        <div key={t.id}
                            className={`flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg animate-slide-in ${colors[t.type]}`}>
                            <Icon size={18} className={`mt-0.5 flex-shrink-0 ${iconColors[t.type]}`} />
                            <p className="text-sm font-medium flex-1">{t.message}</p>
                            <button onClick={() => dismiss(t.id)} className="opacity-50 hover:opacity-100 mt-0.5">
                                <X size={14} />
                            </button>
                        </div>
                    );
                })}
            </div>
        </ToastContext.Provider>
    );
};
